import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import ProfileDropdown from './ProfileDropdown';
import { usePermissions } from '../../hooks/usePermissions';

export type GlobalNavBarVariant = 'dark' | 'light';

interface GlobalNavBarProps {
  variant?: GlobalNavBarVariant;
  maxWidth?: string;
}

const GlobalNavBar: React.FC<GlobalNavBarProps> = ({ variant = 'dark', maxWidth = 'max-w-[1200px]' }) => {
  const { isAuthenticated, user, logout } = useAuthStore();
  const { t } = useTranslation('common');
  const { hasRole } = usePermissions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isInfluencer = user?.userType === 'influencer';
  const isAdmin = hasRole('admin');
  const isDark = variant === 'dark';

  const navLinks: { label: string; to: string }[] = [];

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  /* ── Theme-based classes ── */
  const navBg = isDark ? 'bg-black' : 'bg-white border-b border-gray-200 shadow-sm';
  const logoBg = isDark ? '/matchgo_logo_white.png' : '/matchgo_logo_black.png';
  const linkClass = isDark
    ? 'text-gray-300 hover:text-white text-sm font-medium transition-colors'
    : 'text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors';
  const hamburgerColor = isDark ? 'text-white' : 'text-gray-700';
  const mobilePanelBg = isDark ? 'bg-black border-t border-white/10' : 'bg-white border-t border-gray-200';
  const mobileLinkClass = isDark
    ? 'text-gray-300 hover:text-white text-sm font-medium py-2 transition-colors'
    : 'text-gray-700 hover:text-gray-900 text-sm font-medium py-2 transition-colors';
  const mobileDivider = isDark ? 'border-white/10' : 'border-gray-200';
  const sectionLabelClass = isDark
    ? 'text-xs font-semibold text-gray-500 uppercase tracking-wider'
    : 'text-xs font-semibold text-gray-400 uppercase tracking-wider';

  return (
    <nav className={`sticky top-0 z-50 ${navBg}`}>
      <div className={`${maxWidth} mx-auto px-4 h-16 flex items-center justify-between`}>
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img src={logoBg} alt="MATCHGO" className="h-8" />
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) =>
            link.to.startsWith('#') ? (
              <a key={link.label} href={link.to} className={linkClass}>
                {link.label}
              </a>
            ) : (
              <Link key={link.label} to={link.to} className={linkClass}>
                {link.label}
              </Link>
            )
          )}
        </div>

        {/* Right side: language switcher + auth */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher compact />
          {isAuthenticated ? (
            <ProfileDropdown />
          ) : (
            <>
              <Link
                to="/register"
                className={isDark
                  ? 'px-4 py-1.5 text-sm font-medium text-white bg-white/10 rounded-lg hover:bg-white/20 transition-colors'
                  : 'px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors'}
              >
                {t('mainPage.nav.signUp')}
              </Link>
              <Link
                to="/login"
                className={isDark
                  ? 'px-4 py-1.5 text-sm font-medium text-black bg-white rounded-lg hover:bg-gray-200 transition-colors'
                  : 'px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors'}
              >
                {t('mainPage.nav.logIn')}
              </Link>
            </>
          )}
        </div>

        {/* Mobile: Language switcher + hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
          <button
            className={`${hamburgerColor} p-2`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile slide-down panel */}
      {mobileMenuOpen && (
        <div className={`md:hidden ${mobilePanelBg} px-4 pb-4`}>
          {navLinks.length > 0 && (
            <>
              <p className={`${sectionLabelClass} pt-3 pb-1`}>{t('mainPage.nav.menuLabel', 'Menu')}</p>
              <div className="flex flex-col gap-2 py-1">
                {navLinks.map((link) =>
                  link.to.startsWith('#') ? (
                    <a
                      key={link.label}
                      href={link.to}
                      className={mobileLinkClass}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.to}
                      className={mobileLinkClass}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </div>
            </>
          )}
          <div className={`flex flex-col gap-1 ${navLinks.length > 0 ? `pt-3 border-t ${mobileDivider}` : 'pt-3'}`}>
            {isAuthenticated ? (
              <>
                <p className={`${sectionLabelClass} pb-1`}>{t('mainPage.nav.myAccountLabel', 'My Account')}</p>
                {isAdmin && (
                  <>
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className={`text-sm font-medium py-2 transition-colors ${isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-700 hover:text-blue-800'}`}>
                      {t('profileDropdown.adminDashboard')}
                    </Link>
                    <div className={`border-t ${mobileDivider} my-1`} />
                  </>
                )}
                <Link to="/discovery" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>{t('profileDropdown.allCampaigns')}</Link>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>{t('profileDropdown.dashboard')}</Link>
                <Link to={isInfluencer ? '/my-profile/info' : '/host/info'} onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>{t('profileDropdown.myInfo')}</Link>
                <Link to={isInfluencer ? '/my-profile/profile' : '/host/business-profile'} onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>
                  {isInfluencer ? t('profileDropdown.profile') : t('profileDropdown.businessProfile')}
                </Link>
                <Link to={isInfluencer ? '/my-profile/campaigns' : '/campaigns'} onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>
                  {isInfluencer ? t('profileDropdown.myApplications') : t('profileDropdown.myCampaigns')}
                </Link>
                <Link to={isInfluencer ? '/my-profile/settlement' : '/host/payment'} onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>{t('profileDropdown.settlement')}</Link>
                <div className={`border-t ${mobileDivider} my-1`} />
                <Link to="/notice" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>{t('profileDropdown.notice')}</Link>
                <Link to="/inquiry" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass}>{t('profileDropdown.inquiry')}</Link>
                <div className={`border-t ${mobileDivider} my-1`} />
                <button
                  onClick={() => {
                    if (!window.confirm(t('profileDropdown.logoutConfirm', 'Are you sure you want to logout?'))) return;
                    setMobileMenuOpen(false);
                    logout();
                    navigate('/');
                  }}
                  className={`text-left text-sm font-medium py-2 transition-colors ${isDark ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'}`}
                >
                  {t('profileDropdown.logout')}
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/register"
                  className={`flex-1 text-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isDark ? 'text-white bg-white/10 hover:bg-white/20' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {t('mainPage.nav.signUp')}
                </Link>
                <Link
                  to="/login"
                  className={`flex-1 text-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isDark ? 'text-black bg-white hover:bg-gray-200' : 'text-white bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {t('mainPage.nav.logIn')}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default GlobalNavBar;
